import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import {
  AttentionMessage,
  Box,
  Grid,
  Heading,
  Typography,
  useToast,
  useThemedValueSwitch,
} from '@tq/ui-components';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import { useMapPolygon } from 'api';
import { useIsVisible } from 'hooks';
import { uniqBy } from 'tools';

import { MapConstraintDetail } from '../../../../types/MapConstraintDetail';
import { MapDetailsProps } from './MapDetailsProps';
import { BoundaryHeading } from './BoundaryHeading';
import { MapComponent } from './MapComponent';
import { ExportOptions } from './ExportOptions';
import { EastingNorthing } from './EastingNorthing';
import { NoMapData } from './NoMapData';

export const MapDetails: React.FC<MapDetailsProps> = ({
  applicationReference,
  caseDetails,
  consultation,
  isRpc,
}) => {
  const { t } = useTranslation('case_details_map');
  const toast = useToast();
  const isMobile = useIsMobile();
  const { isTQ } = useThemedValueSwitch();

  const ref = useRef(null);
  const isVisible = useIsVisible(ref);
  const [showMap, setShowMap] = useState(false);

  const { id } = useParams();
  const { data: polygonData } = useMapPolygon(+(id ?? 0));

  const constraints =
    consultation &&
    uniqBy<MapConstraintDetail>(
      consultation.consultationMapConstraintDetails,
      (constraint) => constraint.constraintCode
    );

  useEffect(() => {
    if (isVisible && caseDetails) {
      setShowMap(true);
    }
  }, [isVisible, caseDetails]);

  const handleTileLoadError = useCallback(
    (e) => {
      const toastOptions = {
        id: 'tile-load-error',
        title: t('map_failed_load', 'Map failed to load.'),
      };

      if (e.status === 401 || e.status === 403) {
        return toast({
          ...toastOptions,
          description: t(
            'no_access',
            `You don't have access to this project map. Please contact our help desk if you feel you should.`
          ),
        });
      }

      return toast({
        ...toastOptions,
        description: t(
          'error_occured',
          'An error seems to have occured. Please contact our help desk.'
        ),
      });
    },
    [toast, t]
  );

  return (
    <Grid
      ref={ref}
      gridAutoFlow={{ base: 'row', lg: 'column' }}
      gridAutoColumns='1fr 1fr'
      gridGap={{ base: 0, lg: 6 }}
    >
      <Box>
        {!isMobile && <BoundaryHeading />}
        {polygonData?.geoJson ? (
          <MapComponent {...{ showMap, caseDetails, handleTileLoadError }} />
        ) : (
          <NoMapData caseDetails={caseDetails} />
        )}
      </Box>
      <Box margin={'3rem 1rem 1rem'}>
        <ExportOptions {...{ applicationReference }} />
        {!isMobile && !isTQ && (
          <>
            <Heading size='md' my={4}>
              {t('boundary_site_line', 'Boundary site line')}
            </Heading>
            <EastingNorthing caseDetails={caseDetails} />
          </>
        )}
        {!isRpc && (
          <>
            <Heading size='md' mb={4} gridColumn={{ base: '1 / span 2', lg: '1' }}>
              {t('constraints', 'Constraints')}
            </Heading>
            <Grid gridGap={2}>
              {!constraints || constraints.length === 0 ? (
                <Box>{t('no_constraints_message', 'No data to display')}</Box>
              ) : (
                <AttentionMessage
                  mb={2}
                  description={t(
                    'unique_to_your_organisation_message',
                    'These constraints are unique to your organisation'
                  )}
                />
              )}
              {constraints?.map((constraint, index) => (
                <Box key={index} py={1} px={2} backgroundColor={COLOURS.blue[150]}>
                  <Typography display='inline-block'>{constraint.description}</Typography>
                </Box>
              ))}
            </Grid>
          </>
        )}
      </Box>
    </Grid>
  );
};
